(ns portal.v2-user-table.ocp
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["react-hook-form" :as rhf]
            ["react-use/lib/useDebounce$default" :as useDebounce]
            [cljs-bean.core :refer [->clj]]
            [clojure.string]
            [common.form :as form]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.advertiser :as advertiser.q]
            [common.validators.core :refer [valid-email?]]
            [common.widgets.form-field :refer [form-field]]
            [portal.v2-user-table.queries :as q]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui grant-row [{:keys [grant scope-names on-delete]}]
  (let [grant-id (:id grant)
        role-name (:role-name grant)
        scope-type (:scope-type grant)
        scope-id (:scope-id grant)
        scope-name (get scope-names [scope-type scope-id])]
    ($ Stack
       {:direction "row"
        :alignItems "center"
        :justifyContent "space-between"
        :sx #js {:py 0.5
                 :px 1
                 :borderBottom "1px solid"
                 :borderColor "grey.80"}}
       ($ Box
          ($ Typography {:variant "body2" :fontWeight "bold"}
             (str "Role: " role-name))
          ($ Tooltip {:title (str scope-type " ID: " scope-id)}
             ($ Typography {:variant "caption" :color "text.secondary"}
                (str scope-type ": " (or scope-name scope-id)))))
       ($ IconButton
          {:size "small"
           :color "error"
           :onClick #(on-delete grant-id)
           :data-testid (str "delete-grant-" grant-id)}
          ($ Icon {:fontSize "small"} "close")))))

(defui grant-list [{:keys [grants scope-names on-delete]}]
  (if (seq grants)
    ($ Stack {:spacing 0}
       (for [grant grants]
         ($ grant-row {:key (:id grant)
                       :grant grant
                       :scope-names scope-names
                       :on-delete on-delete})))
    ($ Typography {:variant "body2" :color "text.secondary" :sx #js {:py 1}}
       "No grants assigned")))

(defui add-grant-form [{:keys [user-id roles agency-id advertisers on-add]}]
  (let [{:keys [control setValue reset]}
        (form/use-form #js {:defaultValues #js {:role-id nil
                                                :scope-type nil
                                                :scope-id nil}})

        role-id-watch (rhf/useWatch #js {:control control :name "role-id"})
        scope-type-watch (rhf/useWatch #js {:control control :name "scope-type"})
        scope-id-watch (rhf/useWatch #js {:control control :name "scope-id"})

        role-id (when role-id-watch role-id-watch)
        scope-type (when scope-type-watch scope-type-watch)
        scope-id (when scope-id-watch scope-id-watch)

        roles-by-id (into {} (map (juxt :id identity)) roles)

        role-options (mapv (fn [role]
                             {:value (:id role)
                              :label (:name role)})
                           roles)

        selected-role (get roles-by-id role-id)
        allowed-scopes (set (:allowed-scopes selected-role))

        scope-type-options (cond-> []
                             (contains? allowed-scopes "agency")
                             (conj {:value "agency" :label "Agency"})
                             (contains? allowed-scopes "advertiser")
                             (conj {:value "advertiser" :label "Advertiser"}))

        advertiser-options (mapv (fn [adv]
                                   {:value (:id adv)
                                    :label (:name adv)})
                                 advertisers)

        effective-scope-id (if (= scope-type "agency") agency-id scope-id)

        can-submit? (and role-id scope-type effective-scope-id)]

    ($ Stack {:spacing 2 :sx #js {:pt 2
                                  :borderTop "1px solid"
                                  :borderColor "grey.80"}}
       ($ Typography {:variant "subtitle2"} "Add Grant")
       ($ form-field {:label "Role"}
          ($ form/autocomplete-field
             {:name "role-id"
              :control control
              :on-after-change (fn []
                                 (setValue "scope-type" nil)
                                 (setValue "scope-id" nil))
              :field-props {:options role-options
                            :placeholder "Select role"
                            :data-testid "grant-role-select"}}))
       (when (seq scope-type-options)
         ($ form-field {:label "Scope Type"}
            ($ form/autocomplete-field
               {:name "scope-type"
                :control control
                :on-after-change (fn []
                                   (setValue "scope-id" nil))
                :field-props {:options scope-type-options
                              :placeholder "Select scope type"
                              :data-testid "grant-scope-type-select"}})))
       (when (= scope-type "advertiser")
         ($ form-field {:label "Advertiser"}
            ($ form/autocomplete-field
               {:name "scope-id"
                :control control
                :field-props {:options advertiser-options
                              :placeholder "Select advertiser"
                              :data-testid "grant-scope-id-select"}})))
       ($ Button
          {:type "button"
           :variant "contained"
           :disabled (not can-submit?)
           :onClick (fn []
                      (on-add {:useraccount-id user-id
                               :role-id role-id
                               :scope-type scope-type
                               :scope-id effective-scope-id})
                      (reset))
           :data-testid "add-grant-button"}
          "Add Grant"))))

(defui create-user-grant-row [{:keys [grant roles on-remove]}]
  (let [role-name (:name (first (filter #(= (:id %) (:role-id grant)) roles)))]
    ($ Stack
       {:direction "row"
        :alignItems "center"
        :justifyContent "space-between"
        :sx #js {:py 0.5 :px 1
                 :borderBottom "1px solid"
                 :borderColor "grey.80"}}
       ($ Typography {:variant "body2"}
          (str role-name " (" (:scope-type grant) ")"))
       ($ IconButton
          {:size "small"
           :color "error"
           :onClick on-remove}
          ($ Icon {:fontSize "small"} "close")))))

(defn- validate-wpp-os-email [email set-error clear-errors]
  (when (and (seq email) (valid-email? email))
    (let [email-lower (clojure.string/lower-case email)]
      (-> (q/wpp-os-check-email email)
          (.then (fn [result]
                   (let [{:keys [data]} (->clj result)]
                     (if (and (seq data)
                              (some #(= (clojure.string/lower-case (:email %))
                                        email-lower)
                                    data))
                       (clear-errors "email")
                       (set-error "email" #js {:type "validate"
                                               :message "No user with this email found in WPP OS"})))))
          (.catch (fn [_]
                    (set-error "email" #js {:type "validate"
                                            :message "Failed to verify email against WPP OS"})))))))

(defocp v2-user-create-ocp :v2-user/create []
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {roles :data} (use-query (q/v2-roles-query))
        {advertisers :data} (use-query (advertiser.q/advertisers-query))
        {create-user :mutate is-creating :isPending} (use-mutation (q/create-user-mutation))

        {:keys [control handleSubmit formState setError clearErrors]}
        (form/use-form #js {:mode "onChange"
                            :defaultValues #js {:name ""
                                                :email ""}})

        email-watch (rhf/useWatch #js {:control control :name "email"})

        _ (useDebounce (fn []
                         (validate-wpp-os-email email-watch setError clearErrors))
                       1500
                       #js [email-watch])

        [grants set-grants] (uix/use-state [])

        on-add-grant (uix/use-callback
                      (fn [grant]
                        (set-grants (fn [prev]
                                      (conj prev (dissoc grant :useraccount-id)))))
                      [])

        on-submit (uix/use-callback
                   (fn [data]
                     (let [{:keys [name email]} (->clj data)]
                       (create-user (cond-> {:email email
                                             :grants grants}
                                      (seq name)
                                      (assoc :name name)))))
                   [create-user grants])

        is-valid (.-isValid ^js formState)
        can-submit? (and is-valid (seq grants) (not is-creating))]
    ($ ocp
       {:title "Create a new User"
        :on-submit (handleSubmit on-submit)
        :footer ($ Button
                   {:type "submit"
                    :variant "contained"
                    :disabled (not can-submit?)
                    :data-testid "create-v2-user-button"}
                   "Create User")}
       ($ form-field {:label "Name"}
          ($ form/form-text-field
             {:name "name"
              :control control
              :rules #js {:maxLength #js {:value 255
                                          :message "Name must be 255 characters or less"}}
              :controller-props #js {:placeholder "User name"
                                     :data-testid "v2-user-name-input"}}))
       ($ form-field {:label "Email"}
          ($ form/form-text-field
             {:name "email"
              :control control
              :rules #js {:required "This field is required"
                          :validate #js {:email-format (fn [v]
                                                         (if (valid-email? v)
                                                           true
                                                           "Enter a valid email address"))}}
              :controller-props #js {:placeholder "Email address"
                                     :data-testid "v2-user-email-input"}}))
       ($ form-field {:label "Grants"}
          (if (seq grants)
            ($ Stack {:spacing 0}
               (for [[idx grant] (map-indexed vector grants)]
                 ($ create-user-grant-row
                    {:key idx
                     :grant grant
                     :roles roles
                     :on-remove (fn []
                                  (set-grants (fn [prev]
                                                (into [] (concat (subvec prev 0 idx)
                                                                 (subvec prev (inc idx)))))))})))
            ($ Typography {:variant "body2" :color "text.secondary" :sx #js {:py 1}}
               "No grants added yet")))
       (when (seq roles)
         ($ add-grant-form {:user-id nil
                            :roles roles
                            :agency-id agency-id
                            :advertisers advertisers
                            :on-add on-add-grant})))))

(defocp v2-user-ocp :v2-user/crud [{:keys [user]}]
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {roles :data} (use-query (q/v2-roles-query))
        {advertisers :data} (use-query (advertiser.q/advertisers-query))
        {grants :data} (use-query (q/user-grants-query {:useraccount-id (:id user)}))
        {create-grant :mutate} (use-mutation (q/create-grant-mutation))
        {delete-grant :mutate} (use-mutation (q/delete-grant-mutation))
        scope-names (into {}
                          (map (fn [grant]
                                 (let [scope-type (:scope-type grant)
                                       scope-id (:scope-id grant)
                                       scope-name (case scope-type
                                                    "agency" (get-in grant [:scope :agency :name])
                                                    "advertiser" (get-in grant [:scope :advertiser :name])
                                                    nil)]
                                   [[scope-type scope-id] scope-name])))
                          (:grants user))]
    ($ ocp
       {:title (str "User: " (:name user))
        :disable-form true}
       ($ form-field {:label "Name"}
          ($ Typography {:variant "body1"} (:name user)))
       ($ form-field {:label "Email"}
          ($ Typography {:variant "body1"} (:email user)))
       ($ form-field {:label "Grants"}
          ($ grant-list {:grants grants
                         :scope-names scope-names
                         :on-delete delete-grant}))
       (when (seq roles)
         ($ add-grant-form {:user-id (:id user)
                            :roles roles
                            :agency-id agency-id
                            :advertisers advertisers
                            :on-add create-grant})))))
